import Head from 'next/head';
import dynamic from 'next/dynamic';
import SiteSettings from '~/siteSettings';
import YoutubeBlockParser from '~/components/blockParser/youtubeBlockParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import BidBarrelScriptOnly from '~/components/bidBarrelReact/bidBarrelScriptOnly';
import { getLatestYouTubeVideos, getYouTubePlaylistsFromIdArray } from '~/utils/youtube.server';
import { getYoutubePlaylists } from '~/utils/queries';
const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Videos
 * @category Page
 * @extends Functional Component
 * @summary Display the videos page.
 * @return {Node} React node containing the videos page.
 */

const Videos = props => {
  const { youtubePlaylists } = props;
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
  const title = `Videos | ${ SiteSettings.siteName }`;
  const meta = {
    title: 'Videos | The Drew Barrymore Show',
    desc: 'Watch all the latest videos from the Drew Barrymore Show. Don\'t miss any of Drew\'s News, Drew\'s Cookbook Club and more.'
  }

  return (
    <>
      <Head>
        <title>{ title }</title>
        <meta name='title' content={ meta.title }/>
        <meta property='twitter:title' content={ meta.title } />
        <meta property='og:title' content={ meta.title } />
        <meta name='description' content={ meta.desc } />
        <meta property='twitter:description' content={ meta.desc } />
        <meta property='og:description' content={ meta.desc } />
        <link rel='canonical' href={`${ baseUrl }/videos`} />
        <meta property='og:type' content='video' />
        <meta property='og:url' content={`${ baseUrl }/videos`} />
        <meta property='twitter:site:id' content={ SiteSettings.twitterSiteId } />
        <meta name="keywords" content={ SiteSettings.defaultKeywords } />
      </Head>
      <BidBarrelScriptOnly />
      { youtubePlaylists &&
        <YoutubeBlockParser
          youtubePlaylists={ youtubePlaylists }
        />
      }
      <div className='spacer--3'></div>
      <Utag pageType='video_landing' siteSection='videos'
        data={{
          videoTitle: meta?.title,
        }}
      />
    </>
  );
}

export async function getStaticProps() {
  const data = await getYoutubePlaylists();
  let trays = [];
  try {
    const [latest, playlists] = await Promise.all([
      getLatestYouTubeVideos(),
      getYouTubePlaylistsFromIdArray(data?.entitySubQueueQuery?.items ?? [], 'array'),
    ]);
    trays = [
      { id: 'latest', category: 'Latest', items: latest },
      ...playlists.map((playlist) => ({
        id: playlist.id,
        category: playlist.category,
        items: playlist.items
      }))
    ];
  } catch (e) {
    console.error('Failed to fetch YouTube playlists:', e);
  }

  return {
    props: {
      youtubePlaylists: trays,
    },
    revalidate: 1
  };
}

export default PageWrapper(Videos);
